import React from 'react';
import SECTION_NAMES from 'constants/sectionNames';
import { useSectionsProgressContext } from 'contexts/SectionsProgressContext';
import useAffordaPortalDetailsQuery from 'hooks/queries/useAffordaPortalDetailsQuery';
import { useHouseholdMembersContext } from 'contexts/HouseholdMembersContext';
import useFormStateManager from 'hooks/affordaPortalFormState/useFormStateManager';
import useAsyncPrequalifyOptions from 'pages/ChooseHousehold/hooks';
import { mapIncomeTotals } from 'pages/AffordaWagesAndIncome/submit/mapIncomeTotals';
import useAffordaHouseholdMembers from 'pages/AffordaHousehold/AffordaHousehold.Members.hooks';

const useAffordaApplication = () => {
  const { data: affordaPortalDetails, isLoading } =
    useAffordaPortalDetailsQuery();

  const { isPrimaryUser, filteredMembersWithPendingQualifications } =
    useHouseholdMembersContext();

  const { formState: welcomeAndQualifyForm } = useFormStateManager(
    SECTION_NAMES.WELCOME_AND_QUALIFY,
  );

  const { formState: wagesAndIncomeForm } = useFormStateManager(
    SECTION_NAMES.WAGES_AND_INCOME,
  );

  const { formState: householdForm, updateForm: setHouseholdForm } =
    useFormStateManager(SECTION_NAMES.HOUSEHOLD);

  const { additionalMinorsCount } = useAffordaHouseholdMembers({
    householdForm,
    setHouseholdForm,
  });

  const { formState: assetForm } = useFormStateManager(SECTION_NAMES.ASSETS);
  // eslint-disable-next-line no-unused-vars
  const [propParamError, setPropParamError] = React.useState('');

  const prequalifyOptions = useAsyncPrequalifyOptions(setPropParamError);

  const {
    sectionsProgress: {
      [SECTION_NAMES.HOUSEHOLD]: householdSectionProgress,
      [SECTION_NAMES.WAGES_AND_INCOME]: wagesAndIncomeSectionProgress,
    },
  } = useSectionsProgressContext();

  const termsAndConditionsFlag =
    affordaPortalDetails?.termsAndConditionsAgreedAt;

  const pendingQualifications = React.useMemo(() => {
    if (!filteredMembersWithPendingQualifications?.length) return [];

    // First, collect all members with their incomplete qualifications
    const memberIssues = filteredMembersWithPendingQualifications.reduce(
      (acc, memberQualifications) => {
        const { completedIncomeForm, completedAssetForm, firstName, lastName } =
          memberQualifications?.qualifications || {};

        // Determine which forms are incomplete
        const incompleteIncome = completedIncomeForm === false;
        const incompleteAssets = completedAssetForm === false;

        const issueType =
          incompleteIncome && incompleteAssets
            ? 'afforda.application.qualification.incompleteIncomeAndAssets'
            : incompleteIncome
              ? 'afforda.application.qualification.incompleteIncome'
              : 'afforda.application.qualification.incompleteAssets';

        // Group by issue type
        if (!acc[issueType]) {
          acc[issueType] = { members: [], text: issueType };
        }
        acc[issueType].members.push(`${firstName} ${lastName}`);

        return acc;
      },
      {},
    );

    // Convert to array format
    return Object.values(memberIssues);
  }, [filteredMembersWithPendingQualifications]);

  const isIncomeAssetsNoEligible = React.useMemo(() => {
    if (
      pendingQualifications?.length ||
      !assetForm ||
      !wagesAndIncomeForm ||
      !prequalifyOptions?.length
    )
      return false;

    const { totalAssets } = assetForm;

    const totalIncome = mapIncomeTotals(wagesAndIncomeForm);

    const prequalifyLimit =
      prequalifyOptions[
        welcomeAndQualifyForm?.numberOfPeopleInUnit +
          (additionalMinorsCount ?? 0) -
          1
      ]?.value;

    if (prequalifyLimit === 0) return false;

    const totalIncomeAndAssets = totalIncome + totalAssets;

    return totalIncomeAndAssets > prequalifyLimit;
  }, [
    assetForm,
    pendingQualifications?.length,
    wagesAndIncomeForm,
    additionalMinorsCount,
    prequalifyOptions,
    welcomeAndQualifyForm,
  ]);

  const isWelcomeAndQualifyUneligible = React.useMemo(() => {
    const welcomeNumberOfPeople = welcomeAndQualifyForm?.numberOfPeopleInUnit
      ?.length
      ? +welcomeAndQualifyForm?.numberOfPeopleInUnit
      : 0;

    if (prequalifyOptions?.length < welcomeNumberOfPeople) return true;

    if (welcomeNumberOfPeople === 0 || !prequalifyOptions) return false;

    const prequalifyLimit =
      prequalifyOptions[
        welcomeNumberOfPeople + (additionalMinorsCount ?? 0) - 1
      ]?.value;

    if (prequalifyLimit === 0) return false;

    const welcomeTotalIncome = welcomeAndQualifyForm?.totalAnnualHouseholdIncome
      ?.length
      ? +welcomeAndQualifyForm?.totalAnnualHouseholdIncome
      : 0;

    return welcomeTotalIncome > prequalifyLimit;
  }, [
    welcomeAndQualifyForm?.numberOfPeopleInUnit,
    welcomeAndQualifyForm?.totalAnnualHouseholdIncome,
    prequalifyOptions,
    additionalMinorsCount,
  ]);

  return {
    assetsDisabled: !wagesAndIncomeSectionProgress?.isComplete,
    isLoading,
    isPrimaryApplicant: isPrimaryUser,
    pendingQualifications,
    termsAndConditionsFlag,
    wagesAndIncomeDisabled:
      !householdSectionProgress?.isComplete && isPrimaryUser,
    isWelcomeAndQualifyUneligible,
    isIncomeAssetsNoEligible,
  };
};

export default useAffordaApplication;
